import React from 'react';
import type { ExperienceItem, Bullet } from '../../data/cv';
import { Bullets } from './Bullets';
import { CompanyMark } from './CompanyMark';
import { TechStack } from './TechStack';
import { periodEndpoints, splitLocation, roleDuration } from './helpers';
interface Props {
  item: ExperienceItem;
  isLast: boolean;
  /** One-page mode: trims summary/bullets/stack for a tighter footprint. */
  compact?: boolean;
}
/**
 * Editorial experience entry: a left "spine" carrying the index + year range,
 * and a right content well. On print each entry stays intact.
 */
export function ExperienceEntry({
  item,
  isLast,
  compact = false
}: Props) {
  const {
    start,
    end,
    same
  } = periodEndpoints(item.period);
  const {
    place,
    mode
  } = splitLocation(item.location);
  const duration = roleDuration(item.period);
  // In compact mode, flatten bullets (incl. subsections) and cap the count.
  const compactBullets: Bullet[] = compact ? [...(item.bullets ?? []), ...(item.subsections?.flatMap(s => s.bullets) ?? [])].slice(0, 3) : [];
  return <article className="cv-entry group relative grid grid-cols-[82px_1fr] gap-6" data-id="element-87" data-mp="16wgnl6nziw9f">
      {/* spine: month/year range (clearly stacked) + duration */}
      <div className="flex flex-col items-end pt-[2px] text-right" data-id="element-88" data-mp="1fxwzjiug76fr">
        <span className="whitespace-nowrap font-mono text-[11px] font-medium tabular-nums leading-none text-[var(--ink)]" data-id="element-89" data-mp="1kpg3vavlcui1">
          {end}
        </span>
        {!same && <span className="mt-1 whitespace-nowrap font-mono text-[10px] tabular-nums leading-none text-[var(--ink-faint)]" data-id="element-90" data-mp="1pr5g2tujve7p">
            {start}
          </span>}
        {duration && <span className="mt-2 rounded-full bg-[var(--accent)]/10 px-1.5 py-[1px] font-mono text-[8px] uppercase tracking-[0.1em] text-[var(--accent)]" data-id="element-91" data-mp="1zkul7lb5vqkg">
            {duration}
          </span>}
      </div>

      {/* vertical rule in the gutter between spine and content */}
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 top-2 w-px bg-[var(--line)]" style={{
      left: 'calc(82px + 0.75rem)'
    }} data-id="element-92" data-mp="9at3nl8pu6es">
        {/* node aligned to the header row (monogram centre) */}
        <span className="absolute left-1/2 top-[16px] h-[9px] w-[9px] -translate-x-1/2 rounded-full border-2 border-[var(--accent)] bg-[var(--paper)]" data-id="element-93" data-mp="1lmdf2h76f2rv" />
      </div>

      {/* content */}
      <div className={isLast ? 'pb-0' : 'pb-8'} data-id="element-94" data-mp="1vjrj1hbwxz3a">
        {/* header: monogram + company / role, with meta on the right */}
        <div className="keep-with-next flex items-start gap-3.5" data-id="element-95" data-mp="1qkezb5gc984x">
          <CompanyMark company={item.company} mark={item.mark} domain={item.domain} logo={item.logo} data-id="element-96" data-mp="259ukm5x3old8" />

          <div className="min-w-0 flex-1" data-id="element-97" data-mp="20d497vw8qdy5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5" data-id="element-98" data-mp="2ea9brlwqwuon">
              <h3 className="font-display text-[19px] font-medium leading-tight text-[var(--ink)]" data-id="element-99" data-mp="29c7rwyrhwrc4">
                {item.company}
              </h3>
              <div className="flex items-center gap-2.5" data-id="element-100" data-mp="2syaon9a5ozbw">
                {item.industry && <span className="rounded-full bg-[var(--accent)]/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--accent)]" data-id="element-101" data-mp="2xvt1vbv7i0pp">
                    {item.industry}
                  </span>}
                {mode && <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--ink-faint)]" data-id="element-102" data-mp="2a6q855nasheu">
                    {mode}
                  </span>}
              </div>
            </div>

            <p className="mt-1 text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-soft)]" data-id="element-103" data-mp="20a52qj7d94ah">
              {item.title}
              {place && <span className="ml-2 font-normal normal-case tracking-normal text-[var(--ink-faint)]" data-id="element-104" data-mp="1r8dwnd8wyvg3">
                  · {place}
                </span>}
            </p>
          </div>
        </div>

        {/* body hangs under the header text, aligned past the monogram */}
        <div className="pl-[58px]" data-id="element-105" data-mp="1j7g6zwusy0j1">
          {/* COMPACT: only the first summary sentence + up to 3 flat bullets. */}
          {compact ? <>
              {item.summary?.[0] && <p className="mt-2 max-w-[70ch] text-[11.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-106" data-mp="1afe4zu534c6n">
                  {item.summary[0]}
                </p>}
              {compactBullets.length > 0 && <div className="mt-2 max-w-[70ch] gap-x-10 sm:columns-2" data-id="element-107" data-mp="10gowgrz2w432">
                  <Bullets items={compactBullets} data-id="element-108" data-mp="vhls79qxa1w5" />
                </div>}
              {item.stack && item.stack.length > 0 && <p className="mt-2.5 font-mono text-[9.5px] leading-relaxed text-[var(--ink-faint)]" data-id="element-109" data-mp="rhlbfigcpelx">
                  {item.stack.join(' · ')}
                </p>}
            </> : <>
              {item.summary && <div className="mt-3 max-w-[64ch] space-y-2" data-id="element-110" data-mp="7n1xiagw7tcx">
                  {item.summary.map((p, i) => <p key={i} className="text-[12.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-111" data-mp="cktmowq5l1df" data-mpi={i}>
                      {p}
                    </p>)}
                </div>}

              {item.bullets && <div className="mt-3.5 max-w-[68ch] gap-x-10 sm:columns-2" data-id="element-112" data-mp="3ku3ntmggus7o">
                  <Bullets items={item.bullets} data-id="element-113" data-mp="3prvhom0o8oei" />
                </div>}

              {item.subsections && <div className="mt-4 gap-x-10 sm:columns-2" data-id="element-114" data-mp="3tsp1b372zh1l">
                  {item.subsections.map((sub, i) => <div key={i} className="mb-4 break-inside-avoid last:mb-0" data-id="element-115" data-mp="2p0d9m7qdnzv" data-mpi={i}>
                      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink)]" data-id="element-116" data-mp="192hlxhcomuv6">
                        {sub.heading}
                      </p>
                      <Bullets items={sub.bullets} data-id="element-117" data-mp="3a57kvvt12rtj" />
                    </div>)}
                </div>}

              {/* tech stack — grouped, icon-bearing, consistent across roles */}
              {item.stack && item.stack.length > 0 && <TechStack stack={item.stack} data-id="element-118" data-mp="3k1kaoilgclj0" />}
            </>}
        </div>
      </div>
    </article>;
}